import { useMemo } from "react";

import { create } from "@bufbuild/protobuf";

import Beacon from "@gx/dls/beacons/Beacon";
import FlexWrapper, { AlignItems } from "@gx/dls/containers/FlexWrapper";
import HelpIcon from "@gx/dls/icons/HelpIcon";
import InfiniteTable, { ColumnDef, Row } from "@gx/dls/table/InfiniteTable";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";
import TextShimmer from "@gx/dls/text/TextShimmer";
import Tooltip from "@gx/dls/tooltip/Tooltip";
import Widget, { WidgetVariant } from "@gx/dls/widget/Widget";
import { SourceSyncStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import { ListSourceSyncsRequestSchema } from "@gx/ts-contracts/services/source/api/source_sync_pb";
import { SourceSyncResource } from "@gx/ts-contracts/services/source/resources/source_sync_pb";

import EmptyLayout from "@/layouts/EmptyLayout";

import { useEditSourceFormContext } from "@/pages/sources/components/edit/hooks";
import { shouldRefetchSourceSyncs } from "@/pages/sources/components/edit/utils";
import {
  SYNC_STATUS_TO_BEACON_VARIANT_MAP,
  SYNC_STATUS_TO_TEXT_MAP,
  SYNC_STATUS_TO_TEXT_VARIANT_MAP,
} from "@/pages/sources/constants";

import { useListSourceSyncsQuery } from "@/api/queries/syncs";

import { convertProtobufTimestampToDate, formatDateTime, getTimeAgo } from "@/utils/datetimes";
import { getUserFullName } from "@/utils/users";

import { EditSourceSyncsBrowserbase } from "./browserbase/EditSourceSyncsBrowserbase";

const StatusCell = ({ sync }: { sync: SourceSyncResource }) => {
  const status = sync.syncStatus ?? SourceSyncStatus.UNSPECIFIED;

  return (
    <FlexWrapper gap={6} alignItems={AlignItems.CENTER}>
      <Beacon variant={SYNC_STATUS_TO_BEACON_VARIANT_MAP[status]} isPulse />
      <Text
        size={TextSize.BODY_SM}
        weight={TextWeight.MEDIUM}
        variant={SYNC_STATUS_TO_TEXT_VARIANT_MAP[status]}
      >
        {SYNC_STATUS_TO_TEXT_MAP[status]}
      </Text>
      {sync.errorMessage && (
        <Tooltip body={sync.errorMessage}>
          <HelpIcon />
        </Tooltip>
      )}
    </FlexWrapper>
  );
};

const StatusCellLoading = () => <TextShimmer height={16} width={100} />;

const StartedAtCell = ({ sync }: { sync: SourceSyncResource }) => {
  if (!sync.startedAt) {
    return <Text variant={TextVariant.TERTIARY}>-</Text>;
  }

  const date = convertProtobufTimestampToDate(sync.startedAt);
  return (
    <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_SM}>
      {formatDateTime(date.toISOString())}
    </Text>
  );
};

const StartedAtCellLoading = () => <TextShimmer height={16} width={140} />;

const CompletedAtCell = ({ sync }: { sync: SourceSyncResource }) => {
  if (!sync.completedAt) {
    return <Text variant={TextVariant.TERTIARY}>-</Text>;
  }

  const date = convertProtobufTimestampToDate(sync.completedAt);
  return (
    <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_SM}>
      {getTimeAgo(date)}
    </Text>
  );
};

const CompletedAtCellLoading = () => <TextShimmer height={16} width={100} />;

const CreatedByCell = ({ sync }: { sync: SourceSyncResource }) => {
  if (!sync.createdBy) {
    return <Text variant={TextVariant.TERTIARY}>-</Text>;
  }

  return (
    <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_SM} isEllipsis>
      {getUserFullName(sync.createdBy)}
    </Text>
  );
};

const CreatedByCellLoading = () => <TextShimmer height={16} width={100} />;

const SYNCS_TABLE_COLUMNS: ColumnDef<SourceSyncResource>[] = [
  {
    id: "status",
    header: "Status",
    cellLoading: StatusCellLoading,
    cell: ({ row }) => <StatusCell sync={row.original} />,
  },
  {
    id: "startedAt",
    header: "Started",
    size: 160,
    cellLoading: StartedAtCellLoading,
    cell: ({ row }) => <StartedAtCell sync={row.original} />,
  },
  {
    id: "completedAt",
    header: "Completed",
    size: 160,
    cellLoading: CompletedAtCellLoading,
    cell: ({ row }) => <CompletedAtCell sync={row.original} />,
  },
  {
    id: "createdBy",
    header: "Created by",
    size: 150,
    cellLoading: CreatedByCellLoading,
    cell: ({ row }) => <CreatedByCell sync={row.original} />,
  },
];

const EditSourceSyncs = () => {
  const { state } = useEditSourceFormContext();

  const { data, isLoading } = useListSourceSyncsQuery({
    input: create(ListSourceSyncsRequestSchema, {
      sourceId: state.source.id,
    }),
    options: {
      refetchInterval: (query) => shouldRefetchSourceSyncs(2000, query.state.data?.syncs),
    },
  });

  const syncs = useMemo(() => data?.syncs ?? [], [data?.syncs]);

  const onRowExpand =
    state.source.sourceType === SourceType.BROWSERBASE
      ? (row: Row<SourceSyncResource>) => <EditSourceSyncsBrowserbase sync={row.original} />
      : undefined;

  return (
    <Widget variant={WidgetVariant.BASE} noPadding noHover fillWidth fillHeight>
      <InfiniteTable<SourceSyncResource>
        columns={SYNCS_TABLE_COLUMNS}
        data={syncs}
        getRowId={(sync) => sync.id}
        onRowExpand={onRowExpand}
        contentWhenEmpty={
          <EmptyLayout
            title="No syncs yet"
            description="History will appear here once data syncing begins"
          />
        }
        isLoading={isLoading}
        loadingRowCount={5}
        fillHeight
        fillWidth
      />
    </Widget>
  );
};

export default EditSourceSyncs;
